import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { setAdminAuthFailureHandler, type AdminAuthFailure } from '../lib/api';

/**
 * Admin session state (FO-71).
 *
 * The access token lives ONLY in this module's React state — never in
 * localStorage or sessionStorage. What survives a page reload is an HttpOnly,
 * SameSite=Strict cookie scoped to /api/admin-auth that JavaScript cannot read;
 * on mount we exchange it for a fresh in-memory token via POST /refresh.
 *
 * Residual risk, stated plainly: script running on this origin can still call
 * /admin-auth/refresh (the browser attaches the cookie for it) and can read the
 * in-memory token out of the React tree. What it can NO LONGER do is walk off
 * with a long-lived credential — the old `localStorage.admin_token` WAS the
 * platform's `SUPER_ADMIN_TOKEN`, replayable forever from anywhere, by anyone.
 * Now the token expires on Cognito's clock, is bound to one admin, requires MFA
 * to mint, and every action it performs is attributed to that person. Closing
 * the remaining gap needs the portal on its own origin with a strict CSP, which
 * is a deployment change rather than an application one.
 *
 * First login is the same state machine with two extra stops. An admin invited
 * with `admin-create-user` signs in with the emailed temporary password and is
 * walked through choosing a password and enrolling an authenticator before any
 * token exists:
 *
 *   unauthenticated -> new_password_required -> mfa_enrollment_required
 *                   -> authenticated
 *
 * Nothing about that progress is persisted here, deliberately: every stage is
 * recoverable by signing in again (see api/routes/admin_auth.py), so a stale
 * half-finished state in the browser would be strictly worse than no state.
 */

export type AdminAuthStage =
  | 'loading'
  | 'unauthenticated'
  | 'new_password_required'
  | 'mfa_required'
  | 'mfa_enrollment_required'
  | 'authenticated';

export interface AdminProfile {
  email: string;
  user_id: string;
  display_name?: string | null;
  auth_method: 'cognito' | string;
  pool_mode?: string;
}

export interface PasswordRequirement {
  id: string;
  label: string;
}

export interface PasswordPolicy {
  minimum_length: number;
  requirements: PasswordRequirement[];
  description: string;
}

export interface AdminAuthConfig {
  cognito_login: boolean;
  separate_pool: boolean;
  mode: string;
  /** False only on an opted-in dev box; always true in production. */
  mfa_required: boolean;
  dev_mode?: boolean;
  password_policy?: PasswordPolicy;
}

export interface MfaEnrollment {
  secret_code: string;
  otpauth_uri: string;
  /** Exactly one of these is set; it identifies the enrolment to the server. */
  challenge_ticket?: string | null;
  enrollment_ticket?: string | null;
}

interface AdminAuthContextType {
  isAuthenticated: boolean;
  stage: AdminAuthStage;
  token: string | null;
  admin: AdminProfile | null;
  config: AdminAuthConfig | null;
  pendingEmail: string | null;
  enrollment: MfaEnrollment | null;
  passwordPolicy: PasswordPolicy | null;
  /** Set when the session was ended for us (expiry, MFA gap, lost admin rights). */
  sessionNotice: string | null;
  login: (email: string, password: string) => Promise<void>;
  setNewPassword: (newPassword: string) => Promise<void>;
  submitMfaCode: (code: string) => Promise<void>;
  submitEnrollmentCode: (code: string) => Promise<void>;
  cancelChallenge: () => void;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

const AUTH_BASE = '/api/admin-auth';

/** Pull a useful message out of FastAPI's `detail` (string OR structured object). */
function errorMessage(payload: unknown, fallback: string): string {
  const detail = (payload as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return fallback;
}

/**
 * A rejection the caller can act on rather than just display: the password step
 * needs to know a 400 was "policy", so it can keep the form up and re-render the
 * checklist instead of throwing the admin back to the sign-in screen.
 */
export class AdminAuthError extends Error {
  readonly code: string;
  readonly policy: PasswordPolicy | null;

  constructor(message: string, code: string, policy: PasswordPolicy | null = null) {
    super(message);
    this.name = 'AdminAuthError';
    this.code = code;
    this.policy = policy;
  }
}

async function postJson(path: string, body?: unknown): Promise<Record<string, any>> {
  const response = await fetch(`${AUTH_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // Send/receive the HttpOnly refresh cookie.
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = (payload as { detail?: any } | null)?.detail;
    const structured = detail && typeof detail === 'object' ? detail : null;
    throw new AdminAuthError(
      errorMessage(payload, `Request failed (${response.status})`),
      structured?.error ?? `http_${response.status}`,
      (structured?.password_policy as PasswordPolicy | undefined) ?? null,
    );
  }
  return payload;
}

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [admin, setAdmin] = useState<AdminProfile | null>(null);
  const [stage, setStage] = useState<AdminAuthStage>('loading');
  const [config, setConfig] = useState<AdminAuthConfig | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(null);
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null);
  // Why the last session ended, when it ended for a reason worth showing.
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);

  const refreshTimer = useRef<number | null>(null);
  // Epoch ms at which the in-memory token stops being accepted.
  const expiresAt = useRef<number>(0);
  // The challenge ticket stands in for a Cognito session mid-sign-in. It is
  // opaque, but it is still a credential: it lives in a ref rather than state so
  // it is never part of the rendered tree (and so never lands in a devtools
  // props dump or an error-boundary serialisation).
  const challengeTicket = useRef<string | null>(null);
  // Lets the expiry timer call the latest refresh without a declaration cycle.
  const refreshRef = useRef<() => Promise<boolean>>(async () => false);

  const clearTimer = useCallback(() => {
    if (refreshTimer.current !== null) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const clearSession = useCallback(() => {
    clearTimer();
    challengeTicket.current = null;
    setToken(null);
    setAdmin(null);
    setPendingEmail(null);
    setEnrollment(null);
    setStage('unauthenticated');
  }, [clearTimer]);

  /**
   * Adopt an authenticated response and schedule a silent renewal shortly
   * before the token expires, so a working session never dies mid-click.
   */
  const adoptSession = useCallback(
    (payload: Record<string, any>) => {
      clearTimer();
      challengeTicket.current = null;
      setToken(payload.access_token as string);
      setAdmin((payload.admin ?? null) as AdminProfile | null);
      setPendingEmail(null);
      setEnrollment(null);
      setStage('authenticated');

      const expiresIn = Number(payload.expires_in) || 3600;
      expiresAt.current = Date.now() + expiresIn * 1000;
      // Renew a minute early; never schedule an absurdly tight loop.
      const delayMs = Math.max(30, expiresIn - 60) * 1000;
      refreshTimer.current = window.setTimeout(() => {
        void refreshRef.current();
      }, delayMs);
    },
    [clearTimer],
  );

  const refreshSession = useCallback(async (): Promise<boolean> => {
    try {
      adoptSession(await postJson('/refresh'));
      return true;
    } catch {
      clearSession();
      return false;
    }
  }, [adoptSession, clearSession]);

  useEffect(() => {
    refreshRef.current = refreshSession;
  }, [refreshSession]);

  /**
   * Route a /login, /password/set or /mfa/* response to the right stage.
   *
   * Every step of the chain answers with the same envelope, so the portal never
   * has to know which endpoint it called — only what the server says comes next.
   */
  const handleAuthResponse = useCallback(
    (payload: Record<string, any>, email: string | null) => {
      switch (payload.status) {
        case 'authenticated':
          adoptSession(payload);
          return;
        case 'new_password_required':
          challengeTicket.current = (payload.challenge_ticket as string) ?? null;
          setPendingEmail((payload.email as string) ?? email);
          setEnrollment(null);
          if (payload.password_policy) setPasswordPolicy(payload.password_policy);
          setStage('new_password_required');
          return;
        case 'mfa_required':
          challengeTicket.current = (payload.challenge_ticket as string) ?? null;
          setPendingEmail((payload.email as string) ?? email);
          setEnrollment(null);
          setStage('mfa_required');
          return;
        case 'mfa_enrollment_required':
          challengeTicket.current = null;
          setPendingEmail((payload.email as string) ?? email);
          setEnrollment({
            secret_code: payload.secret_code,
            otpauth_uri: payload.otpauth_uri,
            challenge_ticket: payload.challenge_ticket ?? null,
            enrollment_ticket: payload.enrollment_ticket ?? null,
          });
          setStage('mfa_enrollment_required');
          return;
        default:
          throw new Error('Unexpected response from the sign-in service.');
      }
    },
    [adoptSession],
  );

  // Bootstrap: learn which sign-in methods this deployment offers, then try to
  // resume an existing session from the HttpOnly cookie.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${AUTH_BASE}/config`, { credentials: 'same-origin' });
        if (response.ok && !cancelled) {
          const body = await response.json();
          setConfig(body);
          if (body.password_policy) setPasswordPolicy(body.password_policy);
        }
      } catch {
        /* advisory only — the login form falls back to offering both paths */
      }
      if (cancelled) return;
      const resumed = await refreshRef.current();
      if (!resumed && !cancelled) setStage('unauthenticated');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // A background tab's timers are throttled and a sleeping machine's don't fire
  // at all, so a session that looks alive can be holding a token Cognito has
  // already expired. Re-check on the way back in.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (stage !== 'authenticated') return;
      if (Date.now() < expiresAt.current - 60_000) return;
      void refreshRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [stage]);

  useEffect(() => clearTimer, [clearTimer]);

  const login = useCallback(
    async (email: string, password: string) => {
      handleAuthResponse(await postJson('/login', { email, password }), email);
    },
    [handleAuthResponse],
  );

  /**
   * First login, step 2: exchange the temporary password for one of the admin's
   * own. The response is never a session — on the admin pool it is the MFA
   * enrolment payload, which `handleAuthResponse` moves us straight on to.
   */
  const setNewPassword = useCallback(
    async (newPassword: string) => {
      if (!challengeTicket.current) {
        throw new AdminAuthError(
          'This sign-in step timed out. Please sign in again to restart it.',
          'challenge_expired',
        );
      }
      const payload = await postJson('/password/set', {
        challenge_ticket: challengeTicket.current,
        new_password: newPassword,
      });
      handleAuthResponse(payload, pendingEmail);
    },
    [handleAuthResponse, pendingEmail],
  );

  const submitMfaCode = useCallback(
    async (code: string) => {
      if (!challengeTicket.current) {
        throw new AdminAuthError(
          'Sign-in session expired. Please start again.',
          'challenge_expired',
        );
      }
      const payload = await postJson('/mfa/verify', {
        code,
        challenge_ticket: challengeTicket.current,
      });
      handleAuthResponse(payload, pendingEmail);
    },
    [handleAuthResponse, pendingEmail],
  );

  const submitEnrollmentCode = useCallback(
    async (code: string) => {
      if (!enrollment) {
        throw new AdminAuthError(
          'Enrolment session expired. Please sign in again.',
          'enrollment_expired',
        );
      }
      const payload = await postJson('/mfa/enroll', {
        code,
        challenge_ticket: enrollment.challenge_ticket ?? undefined,
        enrollment_ticket: enrollment.enrollment_ticket ?? undefined,
      });
      handleAuthResponse(payload, pendingEmail);
    },
    [enrollment, handleAuthResponse, pendingEmail],
  );

  const cancelChallenge = useCallback(() => {
    challengeTicket.current = null;
    setPendingEmail(null);
    setEnrollment(null);
    setStage('unauthenticated');
  }, []);

  const logout = useCallback(() => {
    setSessionNotice(null);
    void postJson('/logout').catch(() => undefined);
    clearSession();
  }, [clearSession]);

  /**
   * React to an authorization failure reported by the data API.
   *
   * Previously there was no path from a 401/403 on `/super-admin/*` back into
   * this provider, so a session could remain "authenticated" while every route
   * rejected it — signed in, empty portal, no explanation. Whatever the cause,
   * the portal should either fix itself or say plainly what is wrong.
   */
  const handleAuthFailure = useCallback(
    (failure: AdminAuthFailure) => {
      // 401: the access token is stale. We hold it in memory only, so the
      // refresh cookie is the source of truth — try a silent renewal before
      // troubling the operator. Pages surface their own error meanwhile; a
      // successful renewal means the next call just works.
      if (failure.status === 401) {
        void refreshRef.current().then((renewed) => {
          if (!renewed) {
            setSessionNotice('Your admin session expired. Please sign in again.');
            clearSession();
          }
        });
        return;
      }

      // 403 mfa_enrollment_required: Cognito has no software-token factor
      // recorded for this admin, so every authorised route will keep refusing.
      // Nothing the current session can do about it — a new sign-in re-issues
      // the MFA_SETUP challenge and enrolment then records the factor properly
      // (FO-110). Bounce to sign-in with the reason rather than sitting on a
      // session that cannot act.
      if (failure.code === 'mfa_enrollment_required') {
        setSessionNotice(
          'Multi-factor authentication is not fully set up on your account. ' +
            'Please sign in again to finish enrolling your authenticator.',
        );
        clearSession();
        return;
      }

      // 403 not_an_admin: a real identity without platform-admin rights. This
      // is not recoverable by retrying, and is worth saying out loud.
      if (failure.code === 'not_an_admin') {
        setSessionNotice(
          'This account is not a platform administrator. Ask an existing admin ' +
            'to grant access, then sign in again.',
        );
        clearSession();
        return;
      }

      // Any other 403 is an authorisation decision about one action, not the
      // session — leave the session alone and let the page report it.
    },
    [clearSession],
  );

  // Register for the lifetime of the provider; deregister on unmount so a
  // stale closure cannot clear a session belonging to a later mount.
  useEffect(() => {
    setAdminAuthFailureHandler(handleAuthFailure);
    return () => setAdminAuthFailureHandler(null);
  }, [handleAuthFailure]);

  const value = useMemo<AdminAuthContextType>(
    () => ({
      isAuthenticated: stage === 'authenticated' && Boolean(token),
      stage,
      token,
      admin,
      config,
      pendingEmail,
      enrollment,
      passwordPolicy,
      sessionNotice,
      login,
      setNewPassword,
      submitMfaCode,
      submitEnrollmentCode,
      cancelChallenge,
      logout,
    }),
    [
      admin,
      cancelChallenge,
      config,
      enrollment,
      login,
      logout,
      passwordPolicy,
      pendingEmail,
      sessionNotice,
      setNewPassword,
      stage,
      submitEnrollmentCode,
      submitMfaCode,
      token,
    ],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
